import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { X, UploadSimple, ArrowCounterClockwise, Trash, Bell, Printer } from "@phosphor-icons/react";

export function TrainingRegister({ uid, name, track, label, onClose }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    api.get(`/training/register/${uid}`, { params: { track } }).then(({ data }) => setData(data))
      .catch((e) => toast.error(formatApiError(e)));
  }, [uid, track]);
  useEffect(() => { load(); }, [load]);

  const upload = async (tid, file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/training/register/${uid}/${tid}/proof`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Proof uploaded");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const mark = async (tid, action, kind, key) => {
    try {
      await api.post(`/training/register/${uid}/${tid}/${action}`, { kind, key });
      toast.success(action === "void" ? "Record voided — retained in history" : "Record restored");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remind = async (r) => {
    try {
      const { data: res } = await api.post("/training/remind", { user_id: uid, topic_id: r.topic_id });
      toast.success(res.email === "sent" ? `Reminder emailed to ${name.split(" ")[0]}`
        : `Reminder posted to ${name.split(" ")[0]}'s dashboard (emails are off)`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const base = api.defaults.baseURL;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose} data-testid="training-register-modal">
      <div className="max-h-[85vh] w-full max-w-3xl overflow-y-auto rounded-xl bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h2 className="font-heading text-lg font-bold text-stone-900" data-testid="register-title">{label} register — {name}</h2>
            <p className="mt-0.5 text-xs text-stone-500">Completion dates + submission proof. Red tiles need proof uploaded. Deleting voids a record but keeps it on file.</p>
          </div>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-700" data-testid="register-close"><X size={18} /></button>
        </div>

        {!data ? <p className="text-sm text-stone-400">Loading…</p> : (
          <div className="space-y-3">
            {data.rows.map((r) => {
              const live = r.completions.filter((c) => !c.deleted);
              const liveEv = r.evidence.filter((e) => !e.deleted);
              const dead = [...r.completions.filter((c) => c.deleted).map((c) => ({ kind: "completion", key: c.key, label: `completion ${c.date}` })),
                            ...r.evidence.filter((e) => e.deleted).map((e) => ({ kind: "evidence", key: e.id, label: e.filename }))];
              return (
                <div key={r.topic_id} className="rounded-lg border border-stone-200 p-3" data-testid={`register-row-${r.topic_id}`}>
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold text-stone-800">{r.title}</p>
                        <button onClick={() => remind(r)} title="Send a reminder (email + dashboard task)"
                          className="rounded-full border border-stone-200 p-1 text-stone-400 hover:border-primary hover:text-primary"
                          data-testid={`register-remind-${r.topic_id}`}><Bell size={12} /></button>
                        {live.length === 0 && (
                          <a href={`${base}/training/register/${uid}/${r.topic_id}/paper-packet`}
                            title="Download the paper training + quiz, pre-filled with this employee's info and due date"
                            className="rounded-full border border-stone-200 p-1 text-stone-400 hover:border-primary hover:text-primary"
                            data-testid={`register-paper-${r.topic_id}`}><Printer size={12} /></a>
                        )}
                      </div>
                      {r.last_done && (
                        <p className="mt-0.5 text-[10px] text-stone-400" data-testid={`register-lastdone-${r.topic_id}`}>
                          last done {r.last_done}{r.last_done_as ? ` as “${r.last_done_as}”` : ""}
                        </p>
                      )}
                      <div className="mt-1 flex flex-wrap items-center gap-2">
                        {live.length === 0 && <span className="text-xs text-stone-400">no completion on record</span>}
                        {live.map((c) => (
                          <span key={`${c.key}-${c.original_title || ""}`} className="inline-flex items-center gap-1.5 rounded-full border border-stone-200 bg-stone-50 px-2.5 py-0.5 text-xs text-stone-600" data-testid={`register-completion-${r.topic_id}-${c.key}`}>
                            {c.date}{c.score != null ? ` · ${c.score}%` : ""}{c.source ? ` · ${c.source}` : ""}{c.original_title ? ` · was “${c.original_title}”` : ""}
                            {c.proof_submission && /^(https?:|www\.)/.test(c.proof_submission) && <a href={c.proof_submission.startsWith("http") ? c.proof_submission : `https://${c.proof_submission}`} target="_blank" rel="noreferrer" className="underline decoration-dotted hover:text-primary" data-testid={`register-proof-sub-${r.topic_id}-${c.key}`}>JotForm submission ↗</a>}
                            <button onClick={() => { if (window.confirm(`Void the ${c.date} completion record? It's kept on file, not purged.`)) mark(r.topic_id, "void", "completion", c.key); }}
                              className="text-stone-300 hover:text-[#C0392B]" title="Void (kept on file)" data-testid={`register-void-${r.topic_id}-${c.key}`}><Trash size={11} /></button>
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {liveEv.map((e) => (
                        <div key={e.id} className="group relative" data-testid={`register-proof-${r.topic_id}-${e.id}`}>
                          <a href={`${base}/training/register/file/${uid}/${e.id}`} target="_blank" rel="noreferrer">
                            {(e.content_type || "").startsWith("image/") || e.content_type === "application/pdf"
                              ? <img src={`${base}/training/register/thumb/${uid}/${e.id}`} alt={e.filename} className="h-16 w-20 rounded-md border border-stone-200 object-cover" />
                              : <div className="flex h-16 w-20 items-center justify-center rounded-md border border-stone-200 bg-stone-50 text-[10px] text-stone-400">file</div>}
                          </a>
                          <button onClick={() => { if (window.confirm("Void this proof? It's kept on file, not purged.")) mark(r.topic_id, "void", "evidence", e.id); }}
                            className="absolute -right-1 -top-1 hidden rounded-full bg-white p-0.5 text-stone-400 shadow group-hover:block hover:text-[#C0392B]"
                            title="Void (kept on file)" data-testid={`register-void-ev-${r.topic_id}-${e.id}`}><Trash size={10} /></button>
                        </div>
                      ))}
                      {!r.has_proof && (
                        <label className={`flex h-16 w-28 cursor-pointer flex-col items-center justify-center rounded-md border-2 border-dashed border-[#C0392B]/50 bg-[#C0392B]/5 text-[10px] font-semibold text-[#C0392B] hover:bg-[#C0392B]/10 ${busy ? "opacity-50" : ""}`}
                          data-testid={`register-upload-${r.topic_id}`}>
                          <UploadSimple size={14} className="mb-0.5" /> no proof — upload
                          <input type="file" accept="image/*,application/pdf" className="hidden" disabled={busy}
                            onChange={(e) => { upload(r.topic_id, e.target.files?.[0]); e.target.value = ""; }} />
                        </label>
                      )}
                    </div>
                  </div>
                  {dead.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5 border-t border-dashed border-stone-100 pt-2">
                      {dead.map((d) => (
                        <span key={d.key} className="inline-flex items-center gap-1 rounded-full bg-stone-100 px-2 py-0.5 text-[10px] italic text-stone-400" data-testid={`register-voided-${r.topic_id}-${d.key}`}>
                          voided: {d.label}
                          <button onClick={() => mark(r.topic_id, "restore", d.kind, d.key)} className="text-stone-400 hover:text-[#1e8449]" title="Restore"
                            data-testid={`register-restore-${r.topic_id}-${d.key}`}><ArrowCounterClockwise size={10} /></button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            {data.rows.length === 0 && <p className="text-sm text-stone-400" data-testid="register-empty">No trainings in this track yet.</p>}
          </div>
        )}
      </div>
    </div>
  );
}
